import React, { useEffect, useMemo, useRef, useState } from 'react';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import { usePermissions } from './PermissionsContext';
import { toMiamiDateString } from './utils/dateUtils';
import { METRICS, buildReport, metricValue, sumCells, periodOf, receiptCell } from './utils/goodsOrderedReport';
import './reports.css';

const PERIODS = [{ key: 'day', label: 'Day' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }];
const STATIC_COLS = [
  { key: 'name', label: 'Item', w: 280 },
  { key: 'sku',  label: 'SKU',  w: 130 },
  { key: 'asin', label: 'ASIN', w: 120 },
];
const isDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');

const fmtQty   = (n, unit) => (n == null ? '—' : `${Number(n).toLocaleString('en-US', { maximumFractionDigits: unit === 'lb' ? 2 : 0 })} ${unit}`);
const fmtMoney = (n, perUnit) => (n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: perUnit ? 4 : 2 }));
const fmtCell  = (v, m, unit) => (m.key === 'qty' ? fmtQty(v, unit) : fmtMoney(v, m.perUnit));

// ── Multi-select dropdown (empty selection = all) ─────────────────────────────
function MultiSelect({ label, allLabel, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div className="gor-field gor-ms" ref={ref}>
      <label className="gor-label">{label}</label>
      <button type="button" className={`gor-input gor-ms-btn${open ? ' is-open' : ''}`} onClick={() => setOpen((o) => !o)}>
        <span>{selected.length === 0 ? allLabel : `${selected.length} selected`}</span>
        <span className="gor-caret">▾</span>
      </button>
      {open && (
        <div className="gor-ms-drop">
          <div className="gor-ms-head">
            <span>{selected.length ? `${selected.length} of ${options.length}` : allLabel}</span>
            {selected.length > 0 && <button type="button" className="gor-link" onClick={() => onChange([])}>Clear</button>}
          </div>
          <div className="gor-ms-list">
            {options.map((o) => (
              <label key={o.value} className="gor-check">
                <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                {o.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function GoodsOrderedReportPage() {
  const { can } = usePermissions();
  const today = toMiamiDateString(new Date());

  const [meta,           setMeta]           = useState({ groups: [], suppliers: [] });
  const [from,           setFrom]           = useState(today.slice(0, 8) + '01');
  const [to,             setTo]             = useState(today);
  const [groupsSel,      setGroupsSel]      = useState([]);
  const [suppliersSel,   setSuppliersSel]   = useState([]);
  const [period,         setPeriod]         = useState('month');
  const [staticOn,       setStaticOn]       = useState({ sku: true, asin: true });
  const [metricsOn,      setMetricsOn]      = useState(() => new Set(METRICS.map((m) => m.key)));
  const [includeUndated, setIncludeUndated] = useState(true);
  const [report,         setReport]         = useState(null);   // { receipts, finance, from, to }
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [search,         setSearch]         = useState('');
  const [collapsed,      setCollapsed]      = useState(() => new Set());
  const [expanded,       setExpanded]       = useState(() => new Set());   // items opened to their receipts (Level 3)

  useEffect(() => {
    api.get('/api/reports/goods-ordered/meta')
      .then((r) => setMeta(r.data))
      .catch(() => setError('Failed to load filter options'));
  }, []);

  const datesOk = isDate(from) && isDate(to) && from <= to;

  // Filters apply on Generate; period grouping, columns and search are display-only.
  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/api/reports/goods-ordered', { params: { from, to } });
      const gs = new Set(groupsSel);
      const ss = new Set(suppliersSel);
      const receipts = r.data.receipts.filter((x) => (!gs.size || gs.has(x.group)) && (!ss.size || ss.has(x.supplier)));
      setReport({ receipts, finance: r.data.finance, from, to });
      setCollapsed(new Set());
      setExpanded(new Set());
      setSearch('');
    } catch (err) {
      setError('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const showMoney = report ? report.finance : can('finance');
  const metrics   = METRICS.filter((m) => metricsOn.has(m.key) && (showMoney || !m.money));
  const statics   = STATIC_COLS.filter((c) => c.key === 'name' || staticOn[c.key]);
  const lefts     = statics.map((_, i) => statics.slice(0, i).reduce((s, c) => s + c.w, 0));
  const staticsW  = statics.reduce((s, c) => s + c.w, 0);

  const built = useMemo(() => {
    if (!report) return null;
    const rows = includeUndated ? report.receipts : report.receipts.filter((r) => r.dateSource === 'delivered');
    return { ...buildReport(rows, period), count: rows.length };
  }, [report, period, includeUndated]);
  const undated = report ? report.receipts.filter((r) => r.dateSource !== 'delivered').length : 0;

  const q = search.trim().toLowerCase();
  const matches = (it) => !q || [it.name, it.sku, it.asin].some((v) => (v || '').toLowerCase().includes(q));

  const toggleIn = (setter) => (key) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleGroup  = toggleIn(setCollapsed);
  const toggleMetric = toggleIn(setMetricsOn);
  const toggleItem   = toggleIn(setExpanded);

  return (
    <div className="gor-canvas">
      <HeaderLogged />
      <div className="gor-page">

        <div className="gor-header">
          <h2>Goods Ordered Report</h2>
          <p className="gor-subtitle">Goods received from Material Orders (status Delivered), by delivery date</p>
        </div>

        {error && <div className="gor-error">{error}</div>}

        {/* Filters */}
        <div className="gor-card gor-filters">
          <div className="gor-row">
            <div className="gor-field">
              <label className="gor-label">Date from *</label>
              <input type="date" className="gor-input" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="gor-field">
              <label className="gor-label">Date to *</label>
              <input type="date" className="gor-input" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </div>
            <MultiSelect label="Groups" allLabel="All groups"
              options={meta.groups.map((g) => ({ value: g.key, label: g.label }))} selected={groupsSel} onChange={setGroupsSel} />
            <MultiSelect label="Suppliers" allLabel="All suppliers"
              options={meta.suppliers.map((s) => ({ value: s, label: s }))} selected={suppliersSel} onChange={setSuppliersSel} />
            <div className="gor-field gor-field-btn">
              <button className="gor-btn" onClick={generate} disabled={!datesOk || loading}>
                {loading ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>

          <div className="gor-row gor-row-options">
            <div className="gor-field">
              <label className="gor-label">Columns by</label>
              <div className="gor-seg">
                {PERIODS.map((p) => (
                  <button key={p.key} type="button" className={`gor-seg-btn${period === p.key ? ' is-active' : ''}`} onClick={() => setPeriod(p.key)}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="gor-field">
              <label className="gor-label">Static columns</label>
              <div className="gor-checks">
                {STATIC_COLS.filter((c) => c.key !== 'name').map((c) => (
                  <label key={c.key} className="gor-check">
                    <input type="checkbox" checked={staticOn[c.key]} onChange={(e) => setStaticOn({ ...staticOn, [c.key]: e.target.checked })} />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="gor-field gor-field-grow">
              <label className="gor-label">Period columns</label>
              <div className="gor-checks">
                {METRICS.filter((m) => showMoney || !m.money).map((m) => (
                  <label key={m.key} className="gor-check">
                    <input type="checkbox" checked={metricsOn.has(m.key)} onChange={() => toggleMetric(m.key)} />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {!report && !loading && (
          <div className="gor-empty">Set the period and filters, then click <strong>Generate</strong>.</div>
        )}

        {/* Results */}
        {built && (
          <div className="gor-card gor-results">
            <div className="gor-toolbar">
              <input type="search" className="gor-input gor-search" placeholder="Search by name, SKU or ASIN…"
                value={search} onChange={(e) => setSearch(e.target.value)} />
              <div className="gor-summary">
                <span><strong>{built.count}</strong> receipt{built.count === 1 ? '' : 's'}</span>
                <span>{report.from} – {report.to}</span>
                {undated > 0 && (
                  <label className="gor-check gor-undated"
                    title="Rows delivered before delivery dates were tracked are dated by their estimated delivery date, or by the 1st of the order month">
                    <input type="checkbox" checked={includeUndated} onChange={(e) => setIncludeUndated(e.target.checked)} />
                    Include {undated} receipt{undated === 1 ? '' : 's'} without a delivery date (approx. date)
                  </label>
                )}
              </div>
            </div>

            {built.periods.length === 0 || metrics.length === 0 ? (
              <div className="gor-empty">{metrics.length === 0 ? 'Select at least one period column.' : 'No deliveries in this period.'}</div>
            ) : (
              <div className="gor-table-wrap">
                <table className="gor-table">
                  <thead>
                    <tr>
                      {statics.map((c, i) => (
                        <th key={c.key} rowSpan={2} className="gor-th gor-sticky" style={{ left: lefts[i], minWidth: c.w, maxWidth: c.w }}>{c.label}</th>
                      ))}
                      {built.periods.map((p) => (
                        <th key={p.key} colSpan={metrics.length} className="gor-th gor-th-period">{p.label}</th>
                      ))}
                    </tr>
                    <tr>
                      {built.periods.map((p) => metrics.map((m) => (
                        <th key={`${p.key}|${m.key}`} className="gor-th gor-th-metric" title={m.label}>{m.short}</th>
                      )))}
                    </tr>
                  </thead>
                  <tbody>
                    {built.groups.map((g) => {
                      const items = g.items.filter(matches);
                      if (items.length === 0) return null;
                      const isCollapsed = collapsed.has(g.key);
                      const unit = items[0].unit;
                      return (
                        <React.Fragment key={g.key}>
                          <tr className="gor-group" onClick={() => toggleGroup(g.key)}>
                            <td className="gor-td gor-sticky gor-group-name" colSpan={statics.length} style={{ left: 0 }}>
                              <span className="gor-chevron">{isCollapsed ? '▸' : '▾'}</span>
                              {g.label}
                              <span className="gor-count">{items.length}</span>
                            </td>
                            {built.periods.map((p) => {
                              const cell = sumCells(items, p.key);
                              return metrics.map((m) => (
                                <td key={`${p.key}|${m.key}`} className="gor-td gor-num">
                                  {m.perUnit ? '' : fmtCell(metricValue(cell, m.key), m, unit)}
                                </td>
                              ));
                            })}
                          </tr>
                          {!isCollapsed && items.map((it) => {
                            const itemKey = `${g.key}|${it.key}`;
                            const isOpen = expanded.has(itemKey);
                            return (
                              <React.Fragment key={it.key}>
                                <tr className="gor-item gor-expandable" onClick={() => toggleItem(itemKey)}>
                                  {statics.map((c, i) => (
                                    <td key={c.key} className={`gor-td gor-sticky${c.key === 'name' ? ' gor-name' : ' gor-muted'}`}
                                      style={{ left: lefts[i], minWidth: c.w, maxWidth: c.w }} title={c.key === 'name' ? it.name : undefined}>
                                      {c.key === 'name'
                                        ? <><span className="gor-chevron">{isOpen ? '▾' : '▸'}</span>{it.name}</>
                                        : (it[c.key] || '—')}
                                    </td>
                                  ))}
                                  {built.periods.map((p) => metrics.map((m) => (
                                    <td key={`${p.key}|${m.key}`} className={`gor-td gor-num${it.cells[p.key] ? '' : ' gor-muted'}`}>
                                      {fmtCell(metricValue(it.cells[p.key], m.key), m, it.unit)}
                                    </td>
                                  )))}
                                </tr>
                                {/* Level 3: every delivery of the item, to compare suppliers */}
                                {isOpen && it.receipts.map((r) => {
                                  const cell = receiptCell(r);
                                  return (
                                    <tr key={r.id} className="gor-receipt">
                                      <td className="gor-td gor-sticky gor-name gor-receipt-label" colSpan={statics.length}
                                        style={{ left: 0, maxWidth: staticsW }}>
                                        {periodOf(r.date, 'day').label}
                                        {r.dateSource !== 'delivered' && (
                                          <span className="gor-muted" title="No delivery date recorded: the estimated delivery date or the order month is used"> (approx.)</span>
                                        )}
                                        {' · '}<strong>{r.supplier || 'No supplier'}</strong>
                                        {r.order && <> · Order {r.order}</>}
                                      </td>
                                      {built.periods.map((p) => metrics.map((m) => (
                                        <td key={`${p.key}|${m.key}`} className="gor-td gor-num">
                                          {p.key === r.periodKey ? fmtCell(metricValue(cell, m.key), m, it.unit) : ''}
                                        </td>
                                      )))}
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
